import { Gem, DollarSign } from "lucide-react";
import Image from "next/image";
import { motion } from "framer-motion";
import { cn, formatNumber } from "@/lib/utils";
import { transitions } from "@/lib/motion";
import { asset } from "@/lib/asset";

// ============================================================================
// THE MINT Balance Display
// ============================================================================

interface BalanceDisplayProps {
  coins?: number;
  gems?: number;
  usdCents?: number;
  showAll?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function BalanceDisplay({
  coins = 0,
  gems = 0,
  usdCents = 0,
  showAll = false,
  size = "md",
  className,
}: BalanceDisplayProps) {
  const sizeClasses = {
    sm: {
      container: "gap-2",
      item: "px-2 py-1 gap-1 text-xs",
      icon: "h-3 w-3",
    },
    md: {
      container: "gap-3",
      item: "px-3 py-1.5 gap-1.5 text-sm",
      icon: "h-4 w-4",
    },
    lg: {
      container: "gap-4",
      item: "px-4 py-2 gap-2 text-base",
      icon: "h-5 w-5",
    },
  };

  const sizes = sizeClasses[size];

  const usdDollars = usdCents / 100;

  return (
    <div className={cn("flex items-center", sizes.container, className)}>
      {/* Coins - Gold */}
      {(showAll || coins > 0) && (
        <div
          className={cn(
            "flex items-center rounded-lg bg-gold/10",
            "border border-gold/30",
            sizes.item,
          )}
        >
          <Image
            src={asset("assets/icons/coins.webp")}
            alt="Coins"
            width={16}
            height={16}
            className={sizes.icon}
          />
          <span className="font-serif font-bold text-gold">
            {formatNumber(coins)}
          </span>
        </div>
      )}

      {/* Gems - Gold */}
      {(showAll || gems > 0) && (
        <div
          className={cn(
            "flex items-center rounded-lg bg-gold/10",
            "border border-gold/30",
            sizes.item,
          )}
        >
          <Gem className={cn("text-gold", sizes.icon)} />
          <span className="font-serif font-bold text-gold">
            {formatNumber(gems)}
          </span>
        </div>
      )}

      {/* USD - Verdigris */}
      {(showAll || usdCents > 0) && (
        <div
          className={cn(
            "flex items-center rounded-lg bg-verdigris/10",
            "border border-verdigris/30",
            sizes.item,
          )}
        >
          <DollarSign className={cn("text-verdigris", sizes.icon)} />
          <span className="font-serif font-bold text-verdigris">
            {usdDollars.toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
}

// Compact version for navbar
export function CompactBalance({
  coins = 0,
  gems = 0,
  className,
}: {
  coins?: number;
  gems?: number;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-3 border border-surface-4">
        <Image
          src={asset("assets/icons/coins.webp")}
          alt="Coins"
          width={16}
          height={16}
        />
        <span className="font-serif font-bold text-cream text-sm">
          {formatNumber(coins)}
        </span>
      </div>
      {gems > 0 && (
        <motion.div
          layout
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-3 border border-surface-4"
        >
          <Gem className="h-4 w-4 text-gold" />
          <span className="font-serif font-bold text-cream text-sm">
            {formatNumber(gems)}
          </span>
        </motion.div>
      )}
    </div>
  );
}
